import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { AxiosApiError } from '@/types/apiError';
import { useMutation, useQueryClient, InfiniteData } from '@tanstack/react-query';
import { REPORT_GROUPS_QUERY_KEY } from './useInfiniteReportGroups';
import { REPORT_GROUP_ASSETS_QUERY_KEY } from './useListReportGroupAssets';
import { Asset, ListReportGroupAssetsResponse, ListReportGroupsResponse, ReportGroup } from '@/api/generatedApi';
import { AxiosResponse } from 'axios';

export const useMoveAssetsToGroup = ({
  reportId,
  onSuccess,
  onError,
}: {
  reportId: string;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}) => {
  const queryClient = useQueryClient();
  const { getTenantId } = useTenants();

  const tenantId = getTenantId();

  const { mutate, isPending } = useMutation({
    mutationFn: async ({
      assetIds,
      sourceGroupId,
      targetGroupId,
    }: {
      assetIds: string | string[];
      sourceGroupId: string;
      targetGroupId: string;
    }) => {
      const ids = Array.isArray(assetIds) ? assetIds : [assetIds];
      for (const assetId of ids) {
        await api.reports.addAssetToReportGroup(tenantId, reportId, targetGroupId, { assetId });
        await api.reports.removeAssetFromReportGroup(tenantId, reportId, sourceGroupId, assetId);
      }
    },
    onMutate: async ({ assetIds, sourceGroupId, targetGroupId }) => {
      const ids = Array.isArray(assetIds) ? assetIds : [assetIds];
      const previousData = queryClient.getQueriesData({ queryKey: [REPORT_GROUPS_QUERY_KEY] });
      const previousAssetData = queryClient.getQueriesData({ queryKey: [REPORT_GROUP_ASSETS_QUERY_KEY] });

      queryClient.setQueriesData<InfiniteData<AxiosResponse<ListReportGroupsResponse>>>(
        { queryKey: [REPORT_GROUPS_QUERY_KEY] },
        old => {
          if (!old?.pages) return old;
          return {
            ...old,
            pages: old.pages.map(page => ({
              ...page,
              data: {
                ...page.data,
                groups: page.data.groups.map((group: ReportGroup) => {
                  if (group.id === sourceGroupId) {
                    const updatedAssetIds = group.assetIds.filter(id => !ids.includes(id));
                    return { ...group, assetIds: updatedAssetIds };
                  }
                  if (group.id === targetGroupId) {
                    return { ...group, assetIds: [...group.assetIds, ...ids] };
                  }
                  return group;
                }),
              },
            })),
          };
        }
      );

      queryClient.setQueriesData(
        {
          queryKey: [REPORT_GROUP_ASSETS_QUERY_KEY],
          predicate: query => {
            const key = query.queryKey as unknown[];
            const keyData = key[1] as { groupId?: string } | undefined;
            const matches = keyData?.groupId === sourceGroupId;
            return matches;
          },
        },
        (old: { data: ListReportGroupAssetsResponse }) => {
          if (!old?.data) {
            return old;
          }
          const filteredAssets = (old.data.assets || []).filter((asset: Asset) => !ids.includes(asset.id));
          const result = {
            ...old,
            data: {
              ...old.data,
              assets: filteredAssets,
              pageData: {
                ...old.data.pageData,
                total: Math.max(0, (old.data.pageData?.total || 0) - ids.length),
              },
            },
          };
          return result;
        }
      );

      return { previousData, previousAssetData };
    },
    onError: (error: AxiosApiError, _variables, context) => {
      if (context?.previousData) {
        for (const [queryKey, data] of context.previousData) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      if (context?.previousAssetData) {
        for (const [queryKey, data] of context.previousAssetData) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      onError?.(error.message || 'Failed to move asset(s) to group');
    },
    onSuccess: () => {
      queryClient.resetQueries({ queryKey: [REPORT_GROUP_ASSETS_QUERY_KEY], exact: false });
      onSuccess?.();
    },
  });

  return {
    mutate,
    isPending,
  };
};
